import type { Locale } from '@kilat/shared';

/**
 * Locale-in-URL routing helpers (SEO slice 2b — ADR-0024).
 *
 * URL scheme: the **default locale (en) lives at the bare path** and is the
 * canonical URL (`/`, `/pricing`, `/help/getting-started`, …). Every other
 * supported locale carries a single path-segment prefix (`/ms`, `/zh/pricing`,
 * `/ta/help/getting-started`, …).
 *
 * These are pure string functions so the router, the `<LocaleLink>` wrapper,
 * the `<LocaleSwitcher>`, the `I18nProvider`, and the SEO hook all derive the
 * same URLs from one place (single source of truth). No DOM, no router imports.
 */

/** Supported locales that carry a URL prefix — everything except the default. */
export function prefixedLocales(
  supported: readonly Locale[],
  defaultLocale: Locale,
): readonly Locale[] {
  return supported.filter((l) => l !== defaultLocale);
}

/** First path segment (between the leading and next slash), or ''. */
function firstSegment(pathname: string): string {
  return pathname.split('/')[1] ?? '';
}

/**
 * The active locale implied by a pathname's leading segment. Falls back to the
 * default locale for the bare path or any non-locale first segment (so a bare
 * `/pricing` resolves to en, never treating "pricing" as a locale).
 */
export function localeFromPathname(
  pathname: string,
  supported: readonly Locale[],
  defaultLocale: Locale,
): Locale {
  const seg = firstSegment(pathname);
  const isLocale = (supported as readonly string[]).includes(seg);
  return isLocale && seg !== defaultLocale ? (seg as Locale) : defaultLocale;
}

/**
 * Strip a leading locale prefix, returning the canonical (default-locale) path.
 * `/ms/pricing` → `/pricing`, `/ms` → `/`, `/pricing` → `/pricing` (unchanged).
 */
export function stripLocalePrefix(pathname: string, prefixed: readonly Locale[]): string {
  const segs = pathname.split('/');
  const seg = segs[1] ?? '';
  if ((prefixed as readonly string[]).includes(seg)) {
    const rest = `/${segs.slice(2).join('/')}`;
    return rest === '/' ? '/' : rest.replace(/\/+$/, '') || '/';
  }
  return pathname === '' ? '/' : pathname;
}

/**
 * Add a locale prefix to a bare (default-locale) path. The default locale is
 * returned unprefixed; `/` becomes `/{locale}` for non-default locales.
 */
export function addLocalePrefix(barePath: string, locale: Locale, defaultLocale: Locale): string {
  if (locale === defaultLocale) return barePath;
  return barePath === '/' ? `/${locale}` : `/${locale}${barePath}`;
}

/**
 * Re-target a full pathname to another locale — strip any existing prefix, then
 * re-prefix for the target. Used by the `<LocaleSwitcher>` so tapping "BM" on
 * `/zh/pricing` navigates to `/ms/pricing` (and "English" back to `/pricing`).
 */
export function switchPathnameLocale(
  pathname: string,
  target: Locale,
  supported: readonly Locale[],
  defaultLocale: Locale,
): string {
  const bare = stripLocalePrefix(pathname, prefixedLocales(supported, defaultLocale));
  return addLocalePrefix(bare, target, defaultLocale);
}

/**
 * Prefix an internal `<Link to>` target with the active locale. Non-default
 * locales get the prefix; external/relative/hash targets are left untouched so
 * `mailto:` / `https://` / `#anchor` links keep working.
 */
export function localizeTo(to: string, locale: Locale, defaultLocale: Locale): string {
  if (locale === defaultLocale) return to;
  if (!to.startsWith('/')) return to;
  return to === '/' ? `/${locale}` : `/${locale}${to}`;
}
